/**
 * ListAggregateFacade — typed API over a computed `list` aggregate
 * source.
 *
 * The list row owns aggregation mechanics; consumers should resolve
 * this facade instead of probing `ListDataSource` directly. Typed
 * domain facades such as `EventsFacade` remain attached only to the
 * constituent source rows.
 */

import type { Option } from "../core/option.ts";
import type { NaiveDate } from "@tento-chrono";

import type { CellEntry } from "../core/dtype.ts";
import { FacadeKey, type FacadeFactory } from "./facade.ts";
import { sourceRowReadsOf, type SourceRowReads } from "../rows/row-kind.ts";
import { ListDataSource } from "../rows/variants/list-data-source.ts";
import type { SourceRow } from "../rows/variants/source-row.ts";
import { deepFrozen } from "../core/frozen-collections.ts";
import { Internals } from "../core/internals.ts";

/** The facade key that resolves a `ListAggregateFacade` on a computed `list` row. */
export const LIST_AGGREGATE_FACADE = new FacadeKey<ListAggregateFacade>(
  "listAggregate",
);

/** `ListAggregateFacade`'s friend record (`Internals`): its constructor registers each instance. */
type ListAggregateFacadeInternals = {
  readonly liveRows: (facade: ListAggregateFacade) => readonly SourceRow[];
};
const listAggregateInternals = new Internals<ListAggregateFacade, ListAggregateFacadeInternals>("ListAggregateFacade");

/**
 * The live source rows behind `facade`'s constituents — for the SDK's own
 * providers and front-ends, which read a constituent's variant (its event
 * origin, its data-owning row) and fetch its cells. The facade a read hands
 * out (`row.as(LIST_AGGREGATE_FACADE)`, reached from `handle.model` too)
 * gives the constituents' frozen reads faces only. On
 * `@tento-lona/sheets/internal-ui` and `…/internal-testing`, not on a barrel.
 */
export function liveConstituentRowsOf(facade: ListAggregateFacade): readonly SourceRow[] {
  return listAggregateInternals.of(facade).liveRows(facade);
}

/**
 * The typed read API over a computed `list` row's merged cells, which a data provider resolves
 * instead of probing `ListDataSource`. It hands out reads only: its
 * constituents are their frozen reads faces (`SourceRowReads`), never the
 * live rows.
 */
export class ListAggregateFacade {
  static readonly #internals: ListAggregateFacadeInternals = {
    liveRows: (facade) => facade.#row.constituentRows(),
  };

  readonly #row: ListDataSource;

  constructor(row: ListDataSource) {
    listAggregateInternals.register(this, ListAggregateFacade.#internals);
    this.#row = row;
  }

  /** The list row's constituent source rows, in order, as their frozen reads faces. */
  constituentRows(): readonly SourceRowReads[] {
    return Object.freeze(this.#row.constituentRows().map(sourceRowReadsOf));
  }

  aggregateCells(range: Option<NaiveDate.Range>): readonly CellEntry[] {
    return this.#row.aggregateCells(range);
  }

  async fetchRange<R>(
    range: R,
    fetcher: ListAggregateFacade.RangeFetcher<R>,
  ): Promise<void> {
    const rows = this.constituentRows();
    await Promise.all(rows.map((row) => fetcher(row, range)));
  }
}

/** Types and values that go with `ListAggregateFacade`: `RangeFetcher`, `factory`. */
export namespace ListAggregateFacade {
  /** Loads one constituent's cells for a range; `fetchRange` calls it with each constituent's reads face. */
  export type RangeFetcher<R> = (
    row: SourceRowReads,
    range: R,
  ) => Promise<void>;

  export const factory: FacadeFactory<ListAggregateFacade> = deepFrozen({
    key: LIST_AGGREGATE_FACADE,
    build(row: SourceRow): Option<ListAggregateFacade> {
      if (!(row instanceof ListDataSource)) return null;
      return new ListAggregateFacade(row);
    },
  });
}
